import { Injectable, inject } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { CredoMenuService } from 'credo';
import { DNA_ASSISTANTS } from './dr-future-assistants';
import { marketingPoolOf } from './marketing-pool-access';

/**
 * Global Health navigation — "Health in the Light" (customer decisions 2026-09-20/22).
 *
 * The dashboard is the house; every entry a member needs sits at the top in the
 * order the customer set: Dashboard · Ask Dr. Future · Community · Appointments ·
 * Bookings · Information · Help & Support. Below that come the role-driven areas
 * ("My areas": an entry per protected community group the member holds) and, for
 * administrators only, ONE collapsible "Administration" group — closed by default,
 * because admins show the system to other people and a technical admin nav
 * destroys the health impression.
 *
 * "No role = not rendered": every role-driven entry carries
 * `data-visible: 'role:<key>'` and the shared MenuService drops it — it is never
 * greyed out or locked. The keys are the community role keys the backend sends
 * in `user_payload.community_roles`, and each area links to
 * `/community/g/<group key>`, which the community page resolves to the group's
 * first channel. A new area = one line here and one role/group in the backend
 * seed; nothing in a controller.
 *
 * Shared entries (Dashboard, Community, Bookings, Information, the admin pages)
 * are taken from the credo menu by URL so their labels stay translated in one
 * place; everything Global Health does not offer (statistics, payouts, cards,
 * pools, media, orders) is simply not picked.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalHealthMenuService extends CredoMenuService {
  /** Areas of the community a role opens, in the order they appear under "My areas". */
  private static readonly AREAS: ReadonlyArray<{ group: string; roles: string[]; icon: string; key: string }> = [
    { group: 'practitioners',      roles: ['practitioner'],                                                   icon: 'cil-medical-cross', key: 'menu.areas.practitioners' },
    { group: 'sales_entry',        roles: ['sales_interest', 'sales_l1', 'sales_l2', 'sales_l3', 'leader', 'pack_leader'], icon: 'cil-chart', key: 'menu.areas.sales' },
    { group: 'pack_builders',      roles: ['sales_l1', 'sales_l2', 'sales_l3', 'leader', 'pack_leader'],       icon: 'cil-people',        key: 'menu.areas.pack_builders' },
    { group: 'leaders',            roles: ['leader', 'pack_leader'],                                            icon: 'cil-star',          key: 'menu.areas.leaders' },
    { group: 'pack_leaders',       roles: ['pack_leader'],                                                      icon: 'cil-badge',         key: 'menu.areas.pack_leaders' },
    { group: 'council_of_priests', roles: ['priest'],                                                           icon: 'cil-institution',   key: 'menu.areas.council' },
  ];

  protected override createNavItems(lang: string, t: (key: string) => string): INavData[] {
    const shared = super.createNavItems(lang, t);
    const byUrl = (suffix: string): INavData | undefined =>
      shared.find((item) => typeof item.url === 'string' && item.url.endsWith(suffix));
    const pick = (suffix: string): INavData[] => {
      const item = byUrl(suffix);
      return item ? [item] : [];
    };

    // --- the member's house -------------------------------------------------
    const items: INavData[] = [
      ...pick('/dashboard'),
      // Ask Dr. Future = the AI health-information companion. The human Health
      // Concierge stays reachable through the support hub and Dr. Future's hand-off.
      {
        name: t('dr_future.menu'),
        url: `/${lang}/dr-future`,
        iconComponent: { name: 'cil-compass' },
        // Exact match: the assistants below live under /dr-future/<slug>, and
        // the default prefix match would light this entry up alongside theirs.
        linkProps: { routerLinkActiveOptions: { exact: true } },
      },
      // The DNA-based assistants (customer order 2026-10-09), directly under
      // Dr. Future. Shown to EVERY member — also while locked: the lock is a
      // badge (DrFutureAssistantBadgeService) plus a notice on the page, never a
      // hidden or greyed-out entry. Product names are not translated.
      // Each carries its short explanation (customer 2026-10-09, "Kontext-
      // Erklärungen direkt an den Buttons"): `data-subtitle` is rendered by CSS
      // as a muted line under the name — always, so it works on touch and by
      // keyboard and stays visible while locked; `title` adds the native
      // desktop tooltip and `aria-label` reads name + explanation to a screen
      // reader. Translated like every other label; the product name is not.
      ...DNA_ASSISTANTS.map((a): INavData => {
        const hint = t(`dr_future.assistants.${a.key}.menu_hint`);
        return {
          name: a.name,
          url: `/${lang}/dr-future/${a.slug}`,
          iconComponent: { name: a.menuIcon },
          attributes: { 'data-subtitle': hint, title: hint, 'aria-label': `${a.name} — ${hint}` },
        };
      }),
      ...pick('/community'),
      {
        name: t('appointments.menu'),
        url: `/${lang}/appointments`,
        iconComponent: { name: 'cil-calendar' },
      },
      // DNA Family SPECIAL: only for a qualified SPECIAL buyer. `family_enabled`
      // is an opt-in flag (the layout passes `=== true`), so the entry never shows
      // on a backend that does not send it.
      {
        name: t('menu.my_family'),
        url: `/${lang}/family`,
        iconComponent: { name: 'cil-people' },
        attributes: { 'data-visible': 'flag:family_enabled' },
      },
      // Marketing Pool (spec §1/§14): never public — only with the visibility
      // permission. The flag is set explicitly in filterNavItemsByRole below.
      {
        name: t('marketing_pool.menu'),
        url: `/${lang}/marketing-pool`,
        iconComponent: { name: 'cil-graph' },
        attributes: { 'data-visible': 'flag:marketing_pool_visible' },
      },
      ...pick('/bookings'),
      ...pick('/media/info'),
      {
        name: t('menu.support'),
        url: `/${lang}/support`,
        iconComponent: { name: 'cil-speech' },
      },
    ];

    // --- my areas (role-driven) ----------------------------------------------
    const anyAreaRole = Array.from(new Set(GlobalHealthMenuService.AREAS.flatMap((a) => a.roles))).join('|');
    items.push({
      name: t('menu.my_areas'),
      title: true,
      attributes: { 'data-visible': `role:${anyAreaRole}` },
    });
    GlobalHealthMenuService.AREAS.forEach((area) => {
      items.push({
        name: t(area.key),
        url: `/${lang}/community/g/${area.group}`,
        iconComponent: { name: area.icon },
        attributes: { 'data-visible': `role:${area.roles.join('|')}` },
      });
    });

    // --- administration: one closed group -------------------------------------
    const adminShared = shared.filter((item) => {
      const visible = item.attributes?.['data-visible'];
      const url = typeof item.url === 'string' ? item.url : '';
      const isAdminItem = visible === 'isAdmin' || visible === 'isSuperAdmin' || visible === 'isXshopAdmin';
      return isAdminItem && !GlobalHealthMenuService.HIDDEN_ADMIN_SUFFIXES.some((s) => url.endsWith(s));
    });
    const adminOwn: INavData[] = [
      {
        name: t('appointments.admin_menu'),
        url: `/${lang}/admin/appointments`,
        iconComponent: { name: 'cil-calendar' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        // The folder members see first under Information.
        name: t('sales_toolkit.admin_menu'),
        url: `/${lang}/admin/sales-toolkit`,
        iconComponent: { name: 'cil-folder-open' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        name: t('health_applications'),
        url: `/${lang}/admin/health-applications`,
        iconComponent: { name: 'cil-shield-alt' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        name: t('menu.admin_bundles'),
        url: `/${lang}/admin/bundles`,
        iconComponent: { name: 'cil-gift' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        // DNA Family SPECIAL invitations: list, filter, release a slot (with reason).
        name: t('admin.family.title'),
        url: `/${lang}/admin/family`,
        iconComponent: { name: 'cil-people' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        name: t('menu.admin_bookings'),
        url: `/${lang}/admin/bookings`,
        iconComponent: { name: 'cil-cart' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      {
        // Who uses Dr. Future / the DNA assistants how often, with each member's
        // question/answer history (read-only). Super admin only — the route and
        // the backend enforce it too.
        name: t('menu.admin_dr_future_usage'),
        url: `/${lang}/admin/dr-future-usage`,
        iconComponent: { name: 'cil-speech' },
        attributes: { 'data-visible': 'isSuperAdmin' },
      },
      {
        name: t('bank_reconciliation'),
        url: `/${lang}/admin/bank-reconciliation`,
        iconComponent: { name: 'cil-money' },
        attributes: { 'data-visible': 'isAdmin' },
      },
      // Super admin only — the route and the backend enforce it too.
      {
        name: t('club_special'),
        url: `/${lang}/admin/club-special`,
        iconComponent: { name: 'cil-gift' },
        attributes: { 'data-visible': 'isSuperAdmin' },
      },
      // Marketing Pool admin: for admins, management and every right holder
      // (user.marketing_pool.can_manage); the page hides the tabs a user cannot use.
      {
        name: t('marketing_pool.admin.menu'),
        url: `/${lang}/admin/marketing-pool`,
        iconComponent: { name: 'cil-graph' },
        attributes: { 'data-visible': 'flag:marketing_pool_admin' },
      },
      // WolfPack compensation: a second entrance to the one system that
      // distribution administers as well (the screens live in credo).
      { name: t('wpcp.title'),             url: `/${lang}/admin/wpcp`,        iconComponent: { name: 'cil-star' },          attributes: { 'data-visible': 'isSuperAdmin' } },
      { name: t('wpcp.admin.pool_title'),  url: `/${lang}/admin/wpcp-pool`,   iconComponent: { name: 'cil-dollar' },        attributes: { 'data-visible': 'isSuperAdmin' } },
      { name: t('wpcp.admin.matrix_title'), url: `/${lang}/admin/wpcp-matrix`, iconComponent: { name: 'cil-list-numbered' }, attributes: { 'data-visible': 'isSuperAdmin' } },
    ];
    items.push({
      name: t('menu.administration'),
      // The group's `url` is what CoreUI's samePath() compares with the current
      // route to decide whether the group starts open (dropdownMode "path"). A
      // group WITHOUT a url matches every route ([].every(…) is true) and would
      // always render expanded — so this is the admin prefix, not a link.
      url: `/${lang}/admin`,
      iconComponent: { name: 'cil-settings' },
      // Admins, plus Marketing Pool right holders who are not platform admins
      // (they see only the Marketing Pool entry: every other child is isAdmin /
      // isSuperAdmin and the recursive filter drops it).
      attributes: { 'data-visible': 'flag:gh_admin_area' },
      // Children keep their own data-visible (isSuperAdmin …); the shared
      // MenuService filters the tree recursively and drops an empty group.
      children: [...adminShared, ...adminOwn].map((item) => ({ ...item, badge: undefined })),
    });

    return items;
  }

  private auth = inject(AuthService);

  /**
   * Adds the Marketing Pool flags. The shared layout passes only the generic
   * flags (community_enabled, family_enabled); the menu's `flag:` rule shows an
   * item unless the flag is an explicit `false`, so these must ALWAYS be set to
   * a boolean here — an absent flag would show the Marketing Pool to everyone.
   * Read from the same auth user the layout has just handed over.
   */
  public override filterNavItemsByRole(
    isAdmin: boolean,
    userId?: number,
    isXshopAdmin?: boolean,
    isSuperAdmin?: boolean,
    flags?: Record<string, boolean | undefined>,
    roles?: string[]
  ): void {
    const user = this.auth.getUser();
    const mp = marketingPoolOf(user);
    super.filterNavItemsByRole(isAdmin, userId, isXshopAdmin, isSuperAdmin, {
      ...(flags ?? {}),
      marketing_pool_visible: (user as { marketing_pool_visible?: boolean } | null)?.marketing_pool_visible === true,
      marketing_pool_admin: mp.can_manage,
      gh_admin_area: isAdmin || mp.can_manage,
    }, roles);
  }

  /** Shared admin pages Global Health does not offer. */
  private static readonly HIDDEN_ADMIN_SUFFIXES = [
    '/admin/payouts',
    '/admin/statistics',
    '/admin/ewallet-statistics',
    '/admin/kyc',
    '/admin/card-accounts',
    '/admin/user-pools',
  ];
}
